import type { MetaFunction } from "react-router"

import { ButtonLink } from "~/components/ui/button"
import { BlogLayout, BlogProse } from "~/components/blog/BlogLayout"
import { BlogCta } from "~/components/blog/BlogCta"
import { WrittenBy } from "~/components/blog/WrittenBy"
import { Figure, H2, Lead, List, P, Quote, Strong } from "~/components/blog/prose"
import { getPost, postMeta } from "~/data/posts"

// Buyer-education / conquesting post: how to evaluate a foot traffic data
// provider, with Pine59's deliberately-focused, partner-first pitch.

const post = getPost("how-to-choose-a-foot-traffic-data-provider")!

export const meta: MetaFunction = ({ location }) =>
  postMeta(post, location.pathname)

export default function Post() {
  return (
    <BlogLayout post={post}>
      <BlogProse>
        <WrittenBy post={post} />

        <Lead>
          The location intelligence market has grown significantly. So have the
          options.
        </Lead>
        <P>
          If you're evaluating a new foot traffic data provider, two questions
          may be high on your list:
        </P>
        <P>
          <Strong>1. Am I paying for more than I actually need?</Strong>
        </P>
        <P>And, particularly if you're a consultancy:</P>
        <P>
          <Strong>
            2. Does my location intelligence provider support my client
            relationships and business model?
          </Strong>
        </P>
        <P>We hear both questions frequently.</P>
        <P>
          Pine59 takes a deliberately focused approach. We provide aggregated
          location intelligence about places and populations, with flexible
          access for companies that want to use that intelligence in their own
          analytics, products and client work.
        </P>
        <P>Here's how we'd think about the choice.</P>

        <Figure
          src="/product/docs-guides.webp"
          alt="The Pine59 documentation site: the Guides & examples page, with how-to guides like Brand Comparison, Store Profiling and Area Identification"
          width={1400}
          height={489}
        />

        <BlogCta />

        <H2>Are you paying for more than you need?</H2>
        <P>
          Location intelligence platforms have become increasingly
          sophisticated, combining foot traffic analytics with growing
          ecosystems of datasets, applications, integrations and services.
        </P>
        <P>For many organizations, that breadth is valuable.</P>
        <P>But not everyone needs it.</P>
        <P>
          If what you actually need is reliable foot traffic, trade areas,
          demographics, visitor profiles and other aggregated location
          intelligence, the question is whether you need to pay for a broader
          platform to get it.
        </P>
        <P>Pine59 is deliberately focused.</P>
        <P>
          Our aim isn't to reproduce every feature offered by every location
          intelligence platform. It's to provide high-quality aggregated
          location intelligence at an affordable price, with the flexibility to
          use it where you need it.
        </P>
        <P>
          So if your current subscription or renewal is getting difficult to
          justify, compare what you <Strong>actually use</Strong>, not simply
          feature lists.
        </P>
        <P>
          Switching providers could give you the location intelligence you need
          at a price that makes more sense.
        </P>

        <H2>A different model for consultancies</H2>
        <P>
          There's another reason we're hearing from companies evaluating
          alternatives, particularly consultancies.
        </P>
        <P>
          A consultancy doesn't buy location intelligence primarily to
          understand its own business.
        </P>
        <P>
          <Strong>
            The data is an ingredient in the value it creates for its clients.
          </Strong>
        </P>
        <P>That makes the relationship with the data provider important.</P>
        <P>Pine59 is partner-first by design.</P>
        <P>
          We want consultancies to use our aggregated location intelligence to
          build their own analysis, methodologies and services. Our role is to
          provide the data and intelligence that makes your work better, while
          you build and own the client relationship.
        </P>
        <Quote>Your client. Your analysis. Your relationship.</Quote>
        <P>
          When evaluating a location intelligence provider, don't compare only
          datasets and dashboards. Ask how the provider fits into your business
          model.
        </P>
        <P>
          Can you use the insights in client work? Can you combine them with
          your own analysis and IP? Can you build repeatable services around
          them? And do the commercial terms still work as your client base
          grows?
        </P>
        <P>
          For a consultancy, those questions can matter as much as the data
          itself.
        </P>

        <H2>But does the data stack up?</H2>
        <P>
          Price and partnership only matter if the underlying data is good
          enough.
        </P>
        <P>
          Pine59 builds on more than a decade of location intelligence
          experience.
        </P>
        <P>
          Our offering includes foot traffic, trade areas, demographics, visitor
          profiles, visit length and visitor journeys.
        </P>
        <P>
          In the U.S., Pine59 uses SafeGraph Places as its underlying POI
          dataset, providing extensive coverage from major chains down to
          individual locations, including stores within larger places such as
          malls. You can also draw custom boundaries to analyze the places that
          matter to you.
        </P>
        <P>
          And rather than asking you to trust a carefully selected sales demo,
          we'd prefer you test it yourself.
        </P>

        <Figure
          src="/product/insights-card.webp"
          alt="Pine59 Insights app showing a city's foot traffic trend, visitor demographics and popular days on an interactive map"
          framed
        />

        <H2>Dashboard, data or both?</H2>
        <P>How you access location intelligence matters too.</P>
        <P>Pine59 provides three ways to work with our data:</P>
        <List
          items={[
            <>
              <Strong>Insights</Strong> gives business users a visual platform
              for maps, trends and analysis.
            </>,
            <>
              <Strong>Console</Strong> lets analysts and developers explore the
              underlying datasets.
            </>,
            <>
              <Strong>APIs</Strong> let you integrate Pine59 directly into your
              own analytics, products, data pipelines and AI.
            </>,
          ]}
        />

        <Figure
          src="/product/console.webp"
          alt="The Pine59 Console showing the Demographics dataset schema: field names, types and descriptions"
          framed
        />

        <P>
          This is particularly important for consultancies and data teams.
        </P>
        <P>
          We don't want the value of Pine59 to stop inside a Pine59 dashboard.
          We want our intelligence to become part of the work you're already
          doing.
        </P>

        <H2>Is Pine59 the right alternative for you?</H2>
        <P>The choice comes down to what you actually need.</P>
        <P>
          If you need a broad platform with a large ecosystem of applications,
          datasets and services, Pine59 may not be the right fit.
        </P>
        <P>
          If you're primarily looking for{" "}
          <Strong>
            high-quality location intelligence at a more affordable price
          </Strong>
          , Pine59 is worth evaluating.
        </P>
        <P>
          And if you're a{" "}
          <Strong>
            consultancy looking for a location intelligence partner that
            supports your work and your client relationships
          </Strong>
          , we'd particularly like to talk.
        </P>
        <P>
          Don't take our word for it, test our products and find it for
          yourself.
        </P>
        <P>
          <Strong>
            Bring us the locations and use cases you already know. We'll give
            you access to explore Pine59 against them before you decide.
          </Strong>
        </P>
        <div className="mt-8">
          <ButtonLink href="/get-started" withArrow>
            Test your locations
          </ButtonLink>
        </div>
      </BlogProse>
    </BlogLayout>
  )
}
